/**
 * Writing a guide without seeing tags.
 *
 * The value travels in and out as the HTML a guide is stored as, so this can be
 * swapped for the source box at any moment and the article is the same article.
 * That is the contract the whole thing rests on: `roundTrip.test.ts` walks all
 * twenty-two seeded guides through this document model and back, because a
 * model quietly deletes what it cannot represent.
 *
 * The one subtlety is the loop. The editor emits HTML on every keystroke, the
 * parent stores it, and the parent hands it straight back - so a naive effect
 * would re-parse the document under the caret on every letter and the cursor
 * would jump to the start of the article. `emitted` remembers what we last sent
 * up; anything equal to that is our own echo and is ignored.
 */
import { useEffect, useMemo, useRef } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import { useT } from "../../../../i18n/I18nProvider";
import { kbDocToHtml, kbHtmlToDoc } from "./html";
import { kbExtensions } from "./nodes";
import { KbToolbar } from "./Toolbar";

export function KbRichEditor({ value, onChange }: { value: string; onChange: (html: string) => void }) {
  const t = useT();
  const extensions = useMemo(() => kbExtensions(), []);
  /** The last HTML this editor sent upwards. Its own echo, to be ignored. */
  const emitted = useRef(value);

  const editor = useEditor(
    {
      extensions,
      content: kbHtmlToDoc(value, extensions),
      editorProps: { attributes: { class: "crm-doc crm-kb-rich" } },
      onUpdate: ({ editor: ed }) => {
        const html = kbDocToHtml(ed.getJSON(), extensions);
        emitted.current = html;
        onChange(html);
      },
    },
    [extensions],
  );

  // Something outside changed the body: the HTML tab was edited, or an article
  // was loaded. Re-parsing on our own echo would move the caret to the top.
  useEffect(() => {
    if (!editor || value === emitted.current) return;
    emitted.current = value;
    editor.commands.setContent(kbHtmlToDoc(value, extensions), { emitUpdate: false });
  }, [editor, extensions, value]);

  if (!editor) return <div className="crm-kb-rich-shell">{t("common.loading")}</div>;

  return (
    <div className="crm-kb-rich-shell">
      <KbToolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}
